import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { coversCounty, isPartialCounty } from '@/lib/providerDivisions';
import ServiceSlices from './ServiceSlices';
import { QualificationBadge } from './ProviderDetailCard';

// Restrained COVERAGE legend — intentionally NOT the division colors (those mean services offered).
const COV = { qualified: '#15803d', pending: '#f59e0b', both: '#14b8a6', none: '#e5e7eb' };
const COV_LABEL = { qualified: 'Qualified coverage', pending: 'Potential (Pending)', both: 'Both', none: 'No recorded coverage' };

export default function ProviderCoverageMap({ providers = [], division = '', onOpenProvider }) {
  const [geo, setGeo] = useState(null);
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetch('/fl-counties.json').then((r) => r.json()).then(setGeo).catch(() => {});
  }, []);

  const byCounty = useMemo(() => {
    const map = {};
    (geo?.counties || []).forEach((c) => {
      const qualified = providers.filter((p) => p.qualification_status === 'qualified' && (p.status || 'active') === 'active' && coversCounty(p, c.county, division || null));
      const pending = providers.filter((p) => p.qualification_status !== 'qualified' && coversCounty(p, c.county, division || null));
      let status = 'none';
      if (qualified.length && pending.length) status = 'both';
      else if (qualified.length) status = 'qualified';
      else if (pending.length) status = 'pending';
      map[c.county] = { qualified, pending, status };
    });
    return map;
  }, [geo, providers, division]);

  const counts = useMemo(() => {
    const c = { qualified: 0, pending: 0, both: 0, none: 0 };
    Object.values(byCounty).forEach((v) => { c[v.status] += 1; });
    return c;
  }, [byCounty]);

  return (
    <div data-testid="provider-coverage-map">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-4">
        <Card>
          <CardContent className="p-3">
            <div className="relative">
              {geo ? (
                <svg viewBox={`0 0 ${geo.width} ${geo.height}`} className="mx-auto block h-auto w-full max-h-[46vh]" data-testid="coverage-map-svg"
                  onMouseLeave={() => setHover(null)}>
                  {geo.counties.map((c) => {
                    const info = byCounty[c.county] || { status: 'none' };
                    return (
                      <path key={c.county} d={c.d} fill={COV[info.status]} stroke="#ffffff" strokeWidth={0.8}
                        data-testid={`cov-county-${c.county.replace(/[^a-zA-Z]/g, '')}`}
                        className="cursor-pointer transition-opacity hover:opacity-80"
                        onMouseMove={(e) => {
                          const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
                          setHover({ county: c.county, q: info.qualified?.length || 0, p: info.pending?.length || 0, x: e.clientX - rect.left, y: e.clientY - rect.top });
                        }}
                        onClick={() => setSelected(c.county)} />
                    );
                  })}
                </svg>
              ) : <div className="py-20 text-center text-sm text-slate-400">Loading Florida coverage map…</div>}
              {hover && (
                <div className="pointer-events-none absolute z-10 rounded-md bg-[#0E2A47] px-2.5 py-1.5 text-xs text-white shadow-lg"
                  style={{ left: hover.x + 12, top: hover.y + 12 }}>
                  <div className="font-semibold">{hover.county} County</div>
                  <div className="text-white/80">{hover.q} qualified · {hover.p} pending</div>
                </div>
              )}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Map shows Florida coverage{division ? ` for ${division}` : ''}. Out-of-state providers remain in the directory below.</p>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <div className="text-xs font-bold uppercase tracking-wider text-[#8FC93A]">Coverage</div>
          <div className="space-y-1.5" data-testid="coverage-legend">
            {['qualified', 'both', 'pending', 'none'].map((k) => (
              <div key={k} className="flex items-center gap-2 text-sm">
                <span className="h-3.5 w-3.5 rounded-sm shrink-0" style={{ backgroundColor: COV[k] }} />
                <span className="flex-1 text-slate-700">{COV_LABEL[k]}</span>
                <span className="text-xs text-slate-400">{counts[k]}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">Click a county to list the providers serving it.</p>
        </div>
      </div>

      {selected && (
        <CountyPanel county={selected} info={byCounty[selected]} division={division}
          onClose={() => setSelected(null)}
          onOpenProvider={(p) => { setSelected(null); onOpenProvider?.(p); }} />
      )}
    </div>
  );
}

function ProviderLine({ p, county, onOpenProvider }) {
  return (
    <button onClick={() => onOpenProvider?.(p)} data-testid={`county-provider-${p.id}`}
      className="flex w-full items-center gap-2 rounded-md border border-slate-100 px-2 py-1.5 text-left hover:bg-slate-50">
      <ServiceSlices provider={p} size={24} />
      <span className="flex-1 text-sm font-medium text-[#0E2A47]">{p.company_name}
        {isPartialCounty(p, county) && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700">partial</span>}
      </span>
      <QualificationBadge provider={p} />
    </button>
  );
}

function CountyPanel({ county, info, division, onClose, onOpenProvider }) {
  const qualified = info?.qualified || [];
  const pending = info?.pending || [];
  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="county-coverage-panel">
        <DialogHeader><DialogTitle className="text-[#0E2A47]">{county} County{division ? ` · ${division}` : ''}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-emerald-700">Qualified ({qualified.length})</div>
            {qualified.length ? <div className="space-y-1">{qualified.map((p) => <ProviderLine key={p.id} p={p} county={county} onOpenProvider={onOpenProvider} />)}</div>
              : <p className="text-sm text-slate-400">None qualified here yet.</p>}
          </div>
          <div>
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-amber-700">Registered / Pending ({pending.length})</div>
            {pending.length ? <div className="space-y-1">{pending.map((p) => <ProviderLine key={p.id} p={p} county={county} onOpenProvider={onOpenProvider} />)}</div>
              : <p className="text-sm text-slate-400">No pending registrations here.</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
